import React from 'react';
import { Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';
import VerifiedBadge from './VerifiedBadge';

const TITLES = {
  fr: 'Découvrir des stories', en: 'Discover stories', es: 'Descubrir historias', ar: 'اكتشف القصص',
  pt: 'Descobrir stories', de: 'Stories entdecken', ru: 'Откройте истории', zh: '发现动态',
  ja: 'ストーリーを見つける', hi: 'स्टोरीज़ खोजें', nl: 'Ontdek stories', sw: 'Gundua hadithi', ht: 'Dekouvri stories',
};

const storyThumb = (s) => s?.thumbnail_url || s?.image_url || s?.content_url || s?.media_url || s?.cover_image_url || '';
const isVideo = (s, url) => s?.content_type === 'video' || s?.media_type === 'video' || /\.(mp4|webm|mov)$/i.test(url || '');

export const DiscoverStoryCard = ({ group, onOpen }) => {
  const u = group.user || {};
  const s = group.stories?.[0];
  const url = getMediaUrl(storyThumb(s));
  const avatar = u.profile_image_url || u.avatar_url || u.picture;
  const [avatarBroken, setAvatarBroken] = React.useState(false);
  return (
    <button
      onClick={() => onOpen(group)}
      className="relative flex-shrink-0 w-[112px] h-[180px] rounded-2xl overflow-hidden snap-start bg-gradient-to-br from-violet-800 to-fuchsia-900 border border-white/10 hover:border-fuchsia-400/60 transition-colors text-left group"
      data-testid={`discover-story-${u.id}`}
    >
      {url && (isVideo(s, url) ? (
        <video src={url} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <img src={url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/30" />
      {isVideo(s, url) && (
        <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center">
          <Play className="w-3 h-3 text-white" fill="white" />
        </div>
      )}
      <div className={`absolute top-2 left-2 w-9 h-9 rounded-full p-[2px] ${group.has_unviewed ? 'bg-gradient-to-br from-magenta-500 via-pink-500 to-violet-600' : 'bg-gray-500'}`}>
        <div className="w-full h-full rounded-full bg-black overflow-hidden flex items-center justify-center">
          {avatar && !avatarBroken ? (
            <img
              src={getMediaUrl(avatar)}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setAvatarBroken(true)}
            />
          ) : (
            <span className="text-white text-xs font-bold">{u.name?.[0]?.toUpperCase() || '?'}</span>
          )}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <p className="text-white text-xs font-semibold truncate flex items-center gap-1">
          {u.name?.split(' ')[0] || u.username}
          {u.is_verified && <VerifiedBadge size="xs" />}
        </p>
        {group.stories?.length > 1 && (
          <p className="text-white/60 text-[10px]">{group.stories.length} stories</p>
        )}
      </div>
    </button>
  );
};

export default function DiscoverStoriesSection({ groups = [], offset = 0 }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  if (!groups.length) return null;
  const n = groups.length;
  const rotated = Array.from({ length: Math.min(n, 12) }, (_, i) => groups[(offset + i) % n]);

  const open = (group) => navigate(`/feed?story_user=${group.user.id}`);

  return (
    <section
      className="rounded-3xl bg-white/5 border border-white/10 p-4 backdrop-blur-sm"
      data-testid="discover-stories-section"
    >
      <h3 className="text-base md:text-lg font-bold flex items-center gap-2 mb-3">
        {TITLES[language] || TITLES.en}
      </h3>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
        {rotated.map((g) => <DiscoverStoryCard key={g.user.id} group={g} onOpen={open} />)}
      </div>
    </section>
  );
}
